import type { NavItemBaseProps, NavProps } from "src/components/nav-section";

import { paths } from "src/routes/paths";

import { ICONS, settingIcon } from "./menuIcons";
import { pathsByType } from "./makePaths";

// FS-2026-066: group only the pages already admitted by capability/access checks.
export function groupFederationNav(
  data: NavProps["data"],
  t: (key: string) => string,
): NavProps["data"] {
  const flatten = (items: NavItemBaseProps[]): NavItemBaseProps[] =>
    items.flatMap((item) => (item.children ? flatten(item.children) : [item]));
  const available = new Map(
    flatten(data.flatMap((section) => section.items)).map((item) => [
      item.path,
      item,
    ]),
  );
  const organiser = pathsByType.organiser;
  const federation = paths.dashboard.organiser;
  const page = (path: string, title?: string): NavItemBaseProps[] => {
    const item = available.get(path);
    if (!item) return [];
    available.delete(path);
    return [
      { ...item, icon: undefined, ...(title ? { title: t(title) } : {}) },
    ];
  };
  const group = (
    title: string,
    icon: React.ReactElement,
    children: NavItemBaseProps[],
  ): NavItemBaseProps[] =>
    children.length
      ? [
          {
            title: t(title),
            path: `#${title}`,
            icon,
            children,
          },
        ]
      : [];

  const home = page(organiser.home).map((item) => ({
    ...item,
    icon: ICONS.dashboard,
  }));
  const office = page("/dashboard/organiser/federation-office").map((item) => ({
    ...item,
    icon: ICONS.kanban,
  }));
  const competitions = group("fed_nav_competitions", ICONS.tour, [
    ...page(organiser.tournaments),
    ...page(organiser.rankings),
    ...page(federation.federation_assignments, "fed_nav_assignments"),
    ...page(federation.federation_rulesets, "fed_nav_rulesets"),
    ...page(organiser.livestreaming),
  ]);
  const members = group("fed_nav_members", ICONS.user, [
    ...page("/dashboard/organiser/federation-players", "players"),
    ...page(federation.federation_organisations),
    ...page(organiser.players_and_teams.teams),
    ...page(organiser.players_and_teams.players, "fed_nav_tournament_players"),
    ...page("/dashboard/organiser/federation-licences"),
    ...page("/dashboard/organiser/federation-medical-eligibility"),
    ...page("/dashboard/organiser/federation-transfers", "fed_nav_transfers"),
    ...page("/dashboard/organiser/federation-documents"),
    ...page(paths.dashboard.community_connections),
  ]);
  const finance = group("fed_nav_finance", ICONS.banking, [
    ...page("/dashboard/organiser/federation-finance", "fed_nav_obligations"),
    ...page(organiser.payment_management),
    ...page(organiser.profile.billing_infos),
    ...page(organiser.settings.discounts),
  ]);
  const communications = group("fed_nav_communications", ICONS.mail, [
    ...page(
      "/dashboard/organiser/federation-communications",
      "fed_nav_messages",
    ),
    ...page(organiser.post.root),
    ...page(organiser.post.new, "fed_nav_create_news"),
  ]);
  const reports = group("fed_nav_reports", ICONS.analytics, [
    ...page(organiser.analytics),
    ...page("/dashboard/organiser/federation-audit-reports"),
    ...page(federation.federation_exports, "fed_nav_export"),
  ]);
  const settings = page(
    organiser.settings.associationPortal,
    "federation_general_settings",
  ).map((item) => ({
    ...item,
    path: federation.federation_settings,
    icon: settingIcon,
  }));
  const administration = group("fed_nav_administration", settingIcon, [
    ...page(federation.federation_approvals, "fed_nav_approvals"),
    ...page(organiser.settings.global),
    ...page("/dashboard/organiser/federation-player-registration-settings"),
    ...page(
      "/dashboard/organiser/federation-integrations",
      "fed_nav_integrations",
    ),
    ...page(organiser.adminuser_management),
    ...page(organiser.profile.data),
    ...page(organiser.profile.images),
    ...page(organiser.profile.sports),
  ]);

  return [
    ...(home.length ? [{ subheader: "", items: home }] : []),
    {
      subheader: t("dashboard"),
      items: [
        ...office,
        ...competitions,
        ...members,
        ...finance,
        ...communications,
        ...reports,
        // Retain future/unrecognised pages until they receive an explicit group.
        ...Array.from(available.values()),
        ...settings,
        ...administration,
      ],
    },
  ].filter((section) => section.items.length);
}
